import * as Sentry from '@sentry/vue'
import { isDiagShippingEnabled } from '@/dev/isDiagShippingEnabled'
import { diagnosticContext, diagnosticId } from './context'
import { diagLog } from './diag-trace'
import { flushDiagTrace } from './diag-shipper'

const MARKER_STORAGE = '__spotty_spin_diagnostic_v1'
const MAX_AGE_MS = 24 * 60 * 60 * 1000
type Phase = 'started' | 'api_success' | 'revealing' | 'result_open_requested' | 'result_mounted'
type TerminalPhase = 'completed' | 'cancelled' | 'no_result' | 'api_error' | 'ui_error'

interface Marker {
  version: 1
  attemptId: string
  phase: Phase
  startedAt: number
  updatedAt: number
  context: ReturnType<typeof diagnosticContext>
  action: 'new' | 'check' | 'retry'
  pagehideObserved?: boolean
  spinId?: string
}

function readMarker(): Marker | null {
  try {
    const raw: unknown = JSON.parse(sessionStorage.getItem(MARKER_STORAGE) ?? 'null')
    if (!raw || typeof raw !== 'object') return null
    const marker = raw as Partial<Marker>
    if (
      marker.version !== 1 ||
      typeof marker.attemptId !== 'string' ||
      typeof marker.updatedAt !== 'number' ||
      typeof marker.startedAt !== 'number' ||
      typeof marker.phase !== 'string' ||
      !['started', 'api_success', 'revealing', 'result_open_requested', 'result_mounted'].includes(
        marker.phase
      ) ||
      !marker.context ||
      typeof marker.context.sessionId !== 'string'
    )
      return null
    return marker as Marker
  } catch {
    return null
  }
}

function saveMarker(marker: Marker): void {
  try {
    sessionStorage.setItem(MARKER_STORAGE, JSON.stringify(marker))
  } catch {
    /* non-fatal */
  }
}

function clearMarker(attemptId: string): void {
  try {
    if (readMarker()?.attemptId === attemptId) sessionStorage.removeItem(MARKER_STORAGE)
  } catch {
    /* non-fatal */
  }
}

function record(marker: Marker, phase: string, extra: Record<string, unknown> = {}): void {
  try {
    const data = {
      ...marker.context,
      attemptId: marker.attemptId,
      eventId: diagnosticId(),
      action: marker.action,
      phase,
      spinId: marker.spinId,
      elapsedMs: Date.now() - marker.startedAt,
      ...extra,
    }
    diagLog(`spin.${phase}`, data)
    Sentry.addBreadcrumb({ category: 'spin', message: phase, data, level: 'info' })
    if (isDiagShippingEnabled())
      void flushDiagTrace({
        token: import.meta.env.VITE_DIAG_INGEST_TOKEN,
        releaseSha: import.meta.env.VITE_RELEASE_SHA,
      })
  } catch {
    /* Observability must not break a spin. */
  }
}

export interface SpinDiagnosticAttempt {
  id: string
  stage: (phase: Phase, details?: { spinId?: string }) => void
  finish: (phase: TerminalPhase, reason?: string) => void
  fail: (error: unknown, phase: 'api_error' | 'ui_error') => void
}

export function startSpinDiagnostics(action: Marker['action'] = 'new'): SpinDiagnosticAttempt {
  const marker: Marker = {
    version: 1,
    attemptId: diagnosticId(),
    phase: 'started',
    action,
    startedAt: Date.now(),
    updatedAt: Date.now(),
    context: diagnosticContext(),
  }
  let finished = false
  saveMarker(marker)
  record(marker, 'started')
  const finish: SpinDiagnosticAttempt['finish'] = (phase, reason) => {
    if (finished) return
    finished = true
    record(marker, phase, { reason })
    clearMarker(marker.attemptId)
  }
  return {
    id: marker.attemptId,
    stage(phase, details = {}) {
      if (finished) return
      Object.assign(marker, details, { phase, updatedAt: Date.now() })
      saveMarker(marker)
      record(marker, phase)
    },
    finish,
    fail(error, phase) {
      if (finished) return
      try {
        Sentry.captureException(error, {
          tags: { feature: 'spin', spin_phase: phase, spin_attempt_id: marker.attemptId },
          contexts: { spin: { ...marker.context, action, attemptId: marker.attemptId, phase } },
        })
      } catch {
        /* non-fatal */
      }
      finish(phase, error instanceof Error ? error.name : 'UnknownError')
    },
  }
}

/** Call once per document, before async startup. Storage belongs to this tab only. */
export function initializeSpinDiagnostics(): void {
  const previous = readMarker()
  if (previous) {
    clearMarker(previous.attemptId)
    const ageMs = Date.now() - previous.updatedAt
    if (ageMs >= 0 && ageMs <= MAX_AGE_MS) {
      const details = {
        lastPhase: previous.phase,
        startedAt: previous.startedAt,
        lastUpdatedAt: previous.updatedAt,
        spinId: previous.spinId,
        pagehideObserved: previous.pagehideObserved ?? false,
        interruptionAgeMs: ageMs,
        currentReleaseSha: import.meta.env.VITE_RELEASE_SHA,
      }
      record(previous, 'interrupted', details)
      try {
        Sentry.captureMessage('Spin attempt interrupted', {
          level: 'info',
          tags: {
            feature: 'spin',
            spin_phase: previous.phase,
            spin_attempt_id: previous.attemptId,
          },
          contexts: { spin: { ...previous.context, attemptId: previous.attemptId, ...details } },
        })
      } catch {
        /* non-fatal */
      }
    }
  }
  window.addEventListener('pagehide', () => {
    const marker = readMarker()
    if (marker) saveMarker({ ...marker, pagehideObserved: true })
  })
}
